import React, { useState, useEffect } from 'react';
import { EnvelopeIcon } from '../../../components/ui/Icons';
import { Button, ConfirmationDialog, AlertDialog } from '../../../components/ui';
import { useSendReminder } from '../hooks/useApprovalRequests';
import { API_BASE_URL } from '../../../config/apiConfig';

const SendReminderButton = ({ approvalId, approvalRequest, onSuccess, onError }) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [showAlert, setShowAlert] = useState({ show: false, message: '', type: 'error' });
  const [isSystemEnabled, setIsSystemEnabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const { sendReminder, isSending, error } = useSendReminder(approvalId);

  // Check if email notification system is enabled
  useEffect(() => {
    const readSettingBoolean = async (key) => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/application-setting/key/${key}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) return true;
      const setting = await response.json();
      return setting?.value?.toLowerCase() === 'true';
    };

    const checkSystemStatus = async () => {
      try {
        const [systemEnabled, approvalEnabled] = await Promise.all([
          readSettingBoolean('emailNotificationSystemEnabled'),
          readSettingBoolean('emailNotificationApprovalRequestsEnabled'),
        ]);
        setIsSystemEnabled(systemEnabled && approvalEnabled);
      } catch (error) {
        console.error('Error checking email notification system status:', error);
        setIsSystemEnabled(true); // Default to enabled on error
      }
    };

    checkSystemStatus();
  }, []);

  // Normalize overall approval status to lowercase string
  const rawApprovalStatus = approvalRequest?.status?.name || approvalRequest?.status || '';
  const approvalStatus =
    typeof rawApprovalStatus === 'string'
      ? rawApprovalStatus.toLowerCase()
      : String(rawApprovalStatus).toLowerCase();

  // Count pending approvers (handle both numeric statusId and string/object status)
  const pendingApproversCount =
    approvalRequest?.steps?.filter((step) => {
      const rawStepStatus = step.status?.name || step.status || '';
      const stepStatus =
        typeof rawStepStatus === 'string'
          ? rawStepStatus.toLowerCase()
          : String(rawStepStatus).toLowerCase();

      return step.statusId === 1 || stepStatus === 'pending';
    }).length || 0;

  const isDisabled =
    !isSystemEnabled ||
    !approvalRequest?.emailNotificationApprovalRequestsEnabled ||
    pendingApproversCount === 0 ||
    approvalStatus !== 'pending' ||
    isSending;

  const handleClick = () => {
    if (isDisabled) {
      if (!isSystemEnabled) {
        setShowAlert({
          show: true,
          message: 'Email notification system is disabled. Enable it in Application Settings.',
          type: 'warning',
        });
      } else if (pendingApproversCount === 0) {
        setShowAlert({
          show: true,
          message: 'No pending approvers found for this approval request.',
          type: 'info',
        });
      }
      return;
    }
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    setShowConfirm(false);
    setIsLoading(true);

    try {
      await sendReminder();
      setShowAlert({
        show: true,
        message: 'Reminder emails sent successfully to pending approvers.',
        type: 'success',
      });
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      setShowAlert({
        show: true,
        message: error.message || 'Failed to send reminder emails. Please try again.',
        type: 'error',
      });
      if (onError) {
        onError(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const tooltipText = isSystemEnabled
    ? pendingApproversCount > 0
      ? `Send reminder email to ${pendingApproversCount} pending approver${pendingApproversCount > 1 ? 's' : ''}`
      : 'No pending approvers'
    : 'Email notification system is disabled';

  return (
    <>
      <Button
        onClick={handleClick}
        disabled={isDisabled}
        variant="outline"
        size="sm"
        title={tooltipText}
      >
        <EnvelopeIcon className="h-4 w-4" />
        <span>Send Reminder</span>
      </Button>

      <ConfirmationDialog
        isOpen={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleConfirm}
        title="Send Reminder Emails"
        message={`Are you sure you want to send reminder emails to ${pendingApproversCount} pending approver${pendingApproversCount > 1 ? 's' : ''}?`}
        confirmText="Send Reminder"
        cancelText="Cancel"
        isLoading={isLoading || isSending}
      />

      <AlertDialog
        isOpen={showAlert.show}
        onClose={() => setShowAlert({ show: false, message: '', type: 'error' })}
        title=""
        message={showAlert.message}
        type={showAlert.type}
        confirmText="OK"
      />
    </>
  );
};

export default SendReminderButton;

